/**
 * Binding Resolver
 * Resolves all form bindings and applies computed values to form fields
 */

import type { FormSchema, FormControl } from '@atlas-forms/types-js';
import { DataBindingEngine, type DataSource } from './DataBindingEngine';
import { ExpressionEvaluator, type EvaluatorFunction } from './ExpressionEvaluator';

/**
 * Binding dependency graph
 */
interface BindingDependency {
  fieldId: string;
  dependencies: string[]; // Field IDs this binding depends on
}

/**
 * Resolved value with metadata
 */
export interface ResolvedFieldValue {
  fieldId: string;
  value: any;
  source: 'binding' | 'expression' | 'default' | 'form' | 'data';
  success: boolean;
  error?: string;
  dependencies?: string[];
}

/**
 * Binding resolution options
 */
export interface BindingResolutionOptions {
  /** Whether to resolve default values */
  resolveDefaults?: boolean;

  /** Whether to resolve visibility bindings */
  resolveVisibility?: boolean;

  /** Whether to detect and resolve circular dependencies */
  detectCircularDeps?: boolean;

  /** Maximum recursion depth to prevent infinite loops */
  maxDepth?: number;

  /** Whether to cache results */
  cache?: boolean;
}

/**
 * Binding Resolver - Coordinates binding resolution across forms
 */
export class BindingResolver {
  private engine: DataBindingEngine;
  private resolutionCache: Map<string, ResolvedFieldValue>;
  private dependencyGraph: Map<string, BindingDependency>;
  private resolutionDepth: number;
  private maxResolutionDepth: number;

  constructor(engine?: DataBindingEngine) {
    this.engine = engine || new DataBindingEngine();
    this.resolutionCache = new Map();
    this.dependencyGraph = new Map();
    this.resolutionDepth = 0;
    this.maxResolutionDepth = 100; // Prevent infinite loops
  }

  /**
   * Resolve all bindings in a form
   */
  async resolveForm(
    schema: FormSchema,
    formValues: Record<string, any>,
    options: BindingResolutionOptions = {}
  ): Promise<ResolvedFieldValue[]> {
    // Configure engine
    this.engine.setFormValues(formValues);

    // Build dependency graph
    this.buildDependencyGraph(schema);

    // Resolve in dependency order
    const results: ResolvedFieldValue[] = [];
    const resolved = new Set<string>();
    const resolving = new Set<string>();

    const controls = this.getAllControls(schema);

    for (const control of controls) {
      if (!resolved.has(control.id)) {
        const result = await this.resolveControl(
          control,
          schema,
          formValues,
          resolved,
          resolving,
          options
        );
        if (result) {
          results.push(result);
        }
      }
    }

    return results;
  }

  /**
   * Resolve a single control's bindings
   */
  private async resolveControl(
    control: FormControl,
    schema: FormSchema,
    formValues: Record<string, any>,
    resolved: Set<string>,
    resolving: Set<string>,
    options: BindingResolutionOptions
  ): Promise<ResolvedFieldValue | null> {
    // Check for circular dependencies
    if (options.detectCircularDeps && resolving.has(control.id)) {
      return {
        fieldId: control.id,
        value: null,
        source: 'binding',
        success: false,
        error: `Circular dependency detected for field '${control.id}'`,
      };
    }

    // Check depth limit
    if (this.resolutionDepth >= (options.maxDepth || this.maxResolutionDepth)) {
      return {
        fieldId: control.id,
        value: null,
        source: 'binding',
        success: false,
        error: `Maximum resolution depth exceeded for field '${control.id}'`,
      };
    }

    // Check cache
    if (options.cache && this.resolutionCache.has(control.id)) {
      return this.resolutionCache.get(control.id)!;
    }

    resolving.add(control.id);
    this.resolutionDepth++;

    try {
      let result: ResolvedFieldValue | null = null;

      // Resolve binding
      if (control.binding) {
        result = await this.resolveControlBinding(control, schema, formValues, options);
      }

      // Resolve default value if not already resolved
      if (!result && options.resolveDefaults !== false && control.defaultValue) {
        result = this.resolveDefaultValue(control);
      }

      // Use existing form value as fallback
      if (!result && formValues[control.id] !== undefined) {
        result = {
          fieldId: control.id,
          value: formValues[control.id],
          source: 'form',
          success: true,
        };
      }

      // Default to null if nothing resolved
      if (!result) {
        result = {
          fieldId: control.id,
          value: null,
          source: 'binding',
          success: true,
        };
      }

      resolved.add(control.id);

      // Cache result
      if (options.cache) {
        this.resolutionCache.set(control.id, result);
      }

      return result;
    } finally {
      resolving.delete(control.id);
      this.resolutionDepth--;
    }
  }

  /**
   * Resolve a control's binding
   */
  private async resolveControlBinding(
    control: FormControl,
    _schema: FormSchema,
    _formValues: Record<string, any>,
    _options: BindingResolutionOptions
  ): Promise<ResolvedFieldValue> {
    const binding = control.binding!;

    try {
      const result = await this.engine.resolveBinding(binding);

      // Extract dependencies from expression
      const dependencies = this.extractDependencies(binding.expression || binding.path || '');

      return {
        fieldId: control.id,
        value: result.value,
        source: (result.source || 'binding') as 'binding' | 'expression' | 'default' | 'form' | 'data',
        success: result.success,
        error: result.error,
        dependencies,
      };
    } catch (error) {
      return {
        fieldId: control.id,
        value: null,
        source: 'binding',
        success: false,
        error: String(error),
      };
    }
  }

  /**
   * Resolve a control's default value (may contain expressions)
   */
  private resolveDefaultValue(control: FormControl): ResolvedFieldValue {
    if (control.defaultValue === undefined || control.defaultValue === null) {
      return {
        fieldId: control.id,
        value: control.defaultValue,
        source: 'default',
        success: true,
      };
    }

    // Check if default value is an expression
    if (typeof control.defaultValue === 'string') {
      const cleaned = control.defaultValue.trim();
      if (cleaned.startsWith('{{') && cleaned.endsWith('}}')) {
        try {
          const context = this.engine.getContext();
          const value = ExpressionEvaluator.evaluate(cleaned, context);
          return {
            fieldId: control.id,
            value,
            source: ('expression' as const),
            success: true,
          };
        } catch (error) {
          return {
            fieldId: control.id,
            value: null,
            source: ('expression' as const),
            success: false,
            error: String(error),
          };
        }
      }
    }

    // Return literal default value
    return {
      fieldId: control.id,
      value: control.defaultValue,
      source: 'default',
      success: true,
    };
  }

  /**
   * Resolve field visibility (if binding has a visibility expression)
   */
  async resolveVisibility(
    control: FormControl,
    _options?: BindingResolutionOptions
  ): Promise<boolean> {
    // Check if control has visibility binding
    if (!control.binding) {
      return !control.hidden;
    }

    try {
      const context = this.engine.getContext();

      // If binding has a visibility expression, evaluate it
      // (This would be a custom property on FieldBinding)
      if (control.binding.expression) {
        return ExpressionEvaluator.evaluateCondition(control.binding.expression, context);
      }

      return !control.hidden;
    } catch (error) {
      console.warn(`Failed to resolve visibility for '${control.id}':`, error);
      return !control.hidden;
    }
  }

  /**
   * Resolve field disabled state (based on binding or expression)
   */
  async resolveDisabled(
    control: FormControl,
    _options?: BindingResolutionOptions
  ): Promise<boolean> {
    return control.disabled || false;
  }

  /**
   * Extract field dependencies from an expression or path
   */
  private extractDependencies(expr: string): string[] {
    if (!expr) return [];

    const dependencies = new Set<string>();

    // Simple pattern matching for field references
    // This regex looks for identifiers that could be field names
    const pattern = /\b([a-zA-Z_$][a-zA-Z0-9_$]*)\b/g;
    let match;

    while ((match = pattern.exec(expr)) !== null) {
      const identifier = match[1];

      // Exclude keywords and built-in functions
      if (!this.isKeywordOrBuiltin(identifier)) {
        dependencies.add(identifier);
      }
    }

    return Array.from(dependencies);
  }

  /**
   * Check if an identifier is a keyword or built-in
   */
  private isKeywordOrBuiltin(identifier: string): boolean {
    const keywords = new Set([
      // JavaScript keywords
      'true', 'false', 'null', 'undefined', 'this', 'new', 'typeof', 'instanceof',
      'in', 'of', 'return', 'throw', 'try', 'catch', 'finally', 'if', 'else',
      'for', 'while', 'do', 'switch', 'case', 'break', 'continue', 'default',

      // Built-in objects
      'Math', 'String', 'Array', 'Number', 'Boolean', 'Object', 'Date', 'JSON',
      'RegExp', 'Error', 'Map', 'Set', 'Promise', 'Symbol',

      // Safe functions we allow
      'abs', 'ceil', 'floor', 'round', 'max', 'min', 'pow', 'sqrt', 'trunc',
      'toUpperCase', 'toLowerCase', 'trim', 'substring', 'charAt', 'indexOf',
      'includes', 'startsWith', 'endsWith', 'split', 'join', 'filter', 'map',
    ]);

    return keywords.has(identifier);
  }

  /**
   * Build dependency graph for controls
   */
  private buildDependencyGraph(schema: FormSchema): void {
    const controls = this.getAllControls(schema);

    for (const control of controls) {
      const dependencies = this.extractDependencies(
        control.binding?.expression || control.binding?.path || ''
      );

      this.dependencyGraph.set(control.id, {
        fieldId: control.id,
        dependencies,
      });
    }
  }

  /**
   * Get dependency graph
   */
  getDependencyGraph(): Map<string, BindingDependency> {
    return this.dependencyGraph;
  }

  /**
   * Get all controls from schema
   */
  private getAllControls(schema: FormSchema): FormControl[] {
    const controls: FormControl[] = [];

    if (schema.controls) {
      controls.push(...schema.controls);
    }

    // Sections don't directly contain controls -- nothing to collect from schema.sections.

    return controls;
  }

  /**
   * Register a data source with the engine
   */
  registerDataSource(source: DataSource): void {
    this.engine.registerDataSource(source);
  }

  /**
   * Set form values
   */
  setFormValues(values: Record<string, any>): void {
    this.engine.setFormValues(values);
    this.resolutionCache.clear(); // Clear cache when values change
  }

  /**
   * Set external data
   */
  setData(data: Record<string, any>): void {
    this.engine.setData(data);
    this.resolutionCache.clear();
  }

  /**
   * Register a custom function
   */
  registerFunction(name: string, fn: EvaluatorFunction): void {
    this.engine.registerFunction(name, fn);
  }

  /**
   * Clear caches
   */
  clearCache(): void {
    this.resolutionCache.clear();
    this.engine.clearCache();
  }

  /**
   * Get the underlying binding engine
   */
  getEngine(): DataBindingEngine {
    return this.engine;
  }
}
